import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../lib/axios';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { Card, CardHeader, CardContent } from '../../components/ui/Card';
import { Plus, Edit, Trash2, Loader2, Users, ArrowLeft } from 'lucide-react';

const ManageUsers = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'advisor', status: 'active' });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!currentUser && !formData.password.trim()) newErrors.password = 'Password is required';
    if (!formData.role) newErrors.role = 'Role is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveUser = async () => {
    if (!validateForm()) return;

    try {
      if (currentUser) {
        await api.patch(`/auth/users/${currentUser.id}`, { 
          name: formData.name, 
          email: formData.email, 
          role: formData.role, 
          status: formData.status 
        });
      } else {
        await api.post('/auth/users', formData);
      }
      setModalOpen(false);
      fetchUsers();
    } catch (error) {
      console.error('Failed to save user:', error);
      setErrors({ submit: error.response?.data?.error || 'Failed to save user' });
    }
  };

  const handleEditUser = (user) => {
    setCurrentUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      status: user.status
    });
    setErrors({});
    setModalOpen(true);
  };

  const handleNewUser = () => {
    setCurrentUser(null);
    setFormData({ name: '', email: '', password: '', role: 'advisor', status: 'active' });
    setErrors({});
    setModalOpen(true);
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await api.delete(`/auth/users/${id}`);
      fetchUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  const roleOptions = [
    { value: 'admin', label: 'Admin' },
    { value: 'advisor', label: 'Advisor' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.surface }}>
      <header className="border-b mb-6" style={{ backgroundColor: theme.card, borderColor: theme.border }}>
        <div className="px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: theme.text }}>
                <Users className="w-6 h-6" /> User Management
              </h1>
              <p className="text-sm mt-1" style={{ color: theme.textSecondary }}>Manage admin and advisor accounts</p>
            </div>
            <Button onClick={() => navigate('/admin')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Admin
            </Button>
          </div>
        </div>
      </header>

      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-end mb-6">
            <Button onClick={handleNewUser}>
              <Plus className="w-5 h-5 mr-2" /> Add User
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-20">
              <Loader2 className="w-16 h-16 mx-auto mb-4 animate-spin" style={{ color: theme.primary }} />
              <p className="text-lg" style={{ color: theme.textSecondary }}>Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 mx-auto mb-4 opacity-20" style={{ color: theme.text }} />
              <p className="text-lg" style={{ color: theme.textSecondary }}>No users found</p>
            </div>
          ) : (
            <table className="min-w-full divide-y" style={{ borderColor: theme.border }}>
              <thead>
                <tr>
                  <th scope="col" className="py-3 px-4 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.textSecondary }}>Name</th>
                  <th scope="col" className="py-3 px-4 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.textSecondary }}>Email</th>
                  <th scope="col" className="py-3 px-4 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.textSecondary }}>Role</th>
                  <th scope="col" className="py-3 px-4 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.textSecondary }}>Status</th>
                  <th scope="col" className="py-3 px-4 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.textSecondary }}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: theme.border }}>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td className="py-3 px-4 whitespace-nowrap text-sm font-medium" style={{ color: theme.text }}>{user.name}</td>
                    <td className="py-3 px-4 whitespace-nowrap text-sm" style={{ color: theme.text }}>{user.email}</td>
                    <td className="py-3 px-4 whitespace-nowrap text-sm capitalize" style={{ color: theme.text }}>{user.role}</td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {user.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap text-sm space-x-2">
                      <button onClick={() => handleEditUser(user)} className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
                        <Edit className="w-4 h-4 inline-block" />
                      </button>
                      <button onClick={() => handleDeleteUser(user.id)} className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300">
                        <Trash2 className="w-4 h-4 inline-block" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Modal for Add/Edit User */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="max-w-md w-full">
            <Card>
              <CardHeader>
                <h2 className="text-2xl font-bold" style={{ color: theme.text }}>{currentUser ? 'Edit User' : 'Add New User'}</h2>
              </CardHeader>
              <CardContent>
                <form onSubmit={(e) => { e.preventDefault(); handleSaveUser(); }} noValidate className="space-y-4">
                  {errors.submit && <p className="text-sm text-red-600 dark:text-red-400">{errors.submit}</p>}

                  <Input label="Name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} error={errors.name} required />
                  <Input label="Email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} error={errors.email} required />
                  {!currentUser && (
                    <Input label="Password" type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} error={errors.password} required />
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <Select label="Role" options={roleOptions} value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} error={errors.role} required />
                    <Select label="Status" options={statusOptions} value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} error={errors.status} required />
                  </div>

                  <div className="flex justify-end pt-4 space-x-3">
                    <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
                    <Button type="submit">Save User</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageUsers;
